import { PeopleFlowCombinedReducer, PersistedStateLoadHook } from "."
import { ActionType, ReducerAction } from "../actions/actions"
import {
  IClientRequestInstance,
  ClientRequestsFactory,
  ClientRequestInstanceFactory,
  IClientRequests,
  IDocVerificationTracker,
  DocVerificationTrackerFactory,
  Profile,
} from "../../models"
import { DocValidityRecord } from "../../models/docValidity/docValidityRecord"
import { DataCaptureMode, ProcessModes } from "../../config"
import { ViewModes } from "../../components/SideMenus/DocValiditySideMenu"
import {
  AssociationSettingsRepository,
  MasterSettingsRepository,
  ProfileRepository,
  UsersRepository,
} from "../../repositories"
import {
  AsyncTaskDocExport,
  UiCategoryEnum,
  NavMenuAccess,
  EmploymentStatus,
  ProfileNavMenuAccess,
  AssociationId,
} from "../../types"
import { DocDownloadQueueItem } from "../../screens/DocExports/DocExport"

export interface PerformanceImportTracker {
  fileName: string
  code?: string
  formatErrors?: number
  timestamp: string
  incompleteData?: number
  actualColumns?: string[]
  missingColumns?: string[]
  itemCount?: number
  processing?: boolean
}

export interface ISessionState {
  associationsWithInitialProfileLoad: Record<AssociationId, boolean>
  activeDocumentPortalSection: string
  allClientUsers: { name: string; surname: string; idPassport: string }[]
  allRequests: IClientRequests
  allValidityRecords: DocValidityRecord[]
  comingFromDocValidity: boolean
  currentScreen: UiCategoryEnum
  currentSection: UiCategoryEnum
  dataCaptureMode: DataCaptureMode
  disciplineItemUidToLoad: string
  docVerificationTracker: IDocVerificationTracker
  asyncTaskQueueDocExport: AsyncTaskDocExport[]
  idPassport: string
  isReadOnly: boolean
  itemsDownloading: string[]
  itemsBeingGenerated: DocDownloadQueueItem[]
  lastProfileSaveUnixMs: number
  loading: boolean
  navMenuAccess: NavMenuAccess
  newPassword?: string
  otp?: string
  password: string
  performanceData: string
  performanceImportTracker: PerformanceImportTracker[]
  previousScreen: UiCategoryEnum
  processMode: ProcessModes
  profile: Profile
  profileNavMenuAccess: ProfileNavMenuAccess
  profilePic: string
  selectedAssociation: string
  selectedDocNames: string[]
  selectedEmploymentStatus: EmploymentStatus
  selectedFormIndex: number
  selectedRequest: IClientRequestInstance
  selectedRole: string
  selectedCohort: string
  sourceData: Record<string, any>
  docValidityViewMode: ViewModes | undefined
  docValidityFilterState: any | undefined

  associationRepo?: AssociationSettingsRepository | null
  profileRepo?: ProfileRepository | null
  userRepo?: UsersRepository | null
  masterSettingsRepo?: MasterSettingsRepository | null
}

function defaultState(): ISessionState {
  return {
    associationsWithInitialProfileLoad: {},
    activeDocumentPortalSection: "",
    allClientUsers: [],
    allRequests: ClientRequestsFactory.create([]),
    allValidityRecords: [],
    comingFromDocValidity: false,
    currentScreen: UiCategoryEnum.PROFILES,
    currentSection: UiCategoryEnum.EMPLOYEES,
    dataCaptureMode: DataCaptureMode.STANDARD,
    disciplineItemUidToLoad: "",
    docVerificationTracker: DocVerificationTrackerFactory.create({}),
    asyncTaskQueueDocExport: [],
    idPassport: "",
    isReadOnly: false,
    itemsDownloading: [],
    itemsBeingGenerated: [],
    lastProfileSaveUnixMs: 0,
    loading: false,
    navMenuAccess: {} as NavMenuAccess,
    password: "",
    performanceData: "",
    performanceImportTracker: [],
    previousScreen: UiCategoryEnum.PROFILES,
    processMode: ProcessModes.GENERAL_EMPLOYEE,
    profile: new Profile({}),
    profileNavMenuAccess: {} as ProfileNavMenuAccess,
    profilePic: "",
    selectedAssociation: "",
    selectedCohort: "",
    selectedDocNames: [],
    selectedEmploymentStatus: EmploymentStatus.EMPLOYEE,
    selectedFormIndex: 0,
    selectedRequest: ClientRequestInstanceFactory.create({}),
    selectedRole: "",
    sourceData: {},
    docValidityViewMode: undefined,
    docValidityFilterState: undefined,
    associationRepo: null,
    profileRepo: null,
    userRepo: null,
    masterSettingsRepo: null,
  }
}

const propsToResetOnViewChange = {
  docValidityViewMode: undefined,
  docValidityFilterState: undefined,
}

export default function reducer(state: ISessionState = defaultState(), action: ReducerAction): ISessionState {
  switch (action.type) {
    case ActionType.UPDATE_STATE:
      return { ...state, ...action.data }

    case ActionType.RECORD_ASSOCIATION_INITIAL_PROFILE_LOAD:
      return {
        ...state,
        associationsWithInitialProfileLoad: {
          ...state.associationsWithInitialProfileLoad,
          [action.data.association]: true,
        },
      }

    case ActionType.INITIALISE_LOGIN_STATE:
      return {
        ...state,
        ...propsToResetOnViewChange,
        itemsBeingGenerated: [],
        associationsWithInitialProfileLoad: {},
        currentSection: UiCategoryEnum.LOGIN,
        currentScreen: UiCategoryEnum.LOGIN,
        loading: false,
        idPassport: "",
        password: "",
      }

    case ActionType.CHANGE_SCREEN:
      return { ...state, ...action.data }

    case ActionType.SET_SELECTED_ASSOCIATION:
      return {
        ...state,
        ...propsToResetOnViewChange,
        selectedAssociation: action.data.selectedAssociation.toLowerCase(),
      }

    case ActionType.SET_SELECTED_COHORT:
      if (!action.data.selectedCohort) {
        return { ...state }
      }
      return { ...state, ...propsToResetOnViewChange, selectedCohort: action.data.selectedCohort }

    default:
      return { ...state }
  }
}

// JSON.stringify strips class prototypes, so anything restored from the persisted
// snapshot comes back as a plain object and every method call on it throws.
const rehydrateProfile = (profile: any): Profile => {
  if (profile instanceof Profile) {
    return profile
  }
  if (!profile || typeof profile !== "object") {
    return new Profile({})
  }
  return new Profile(profile, profile.pk)
}

const rehydrateValidityRecords = (records: any): DocValidityRecord[] => {
  if (!Array.isArray(records)) {
    return []
  }
  return records.map((record) => (record instanceof DocValidityRecord ? record : new DocValidityRecord(record)))
}

export const onSessionManagerLoad: PersistedStateLoadHook = (state: PeopleFlowCombinedReducer) => {
  if (!state.sessionManager) {
    return { ...state }
  }
  let sessionManager = { ...state.sessionManager }
  sessionManager.docVerificationTracker = DocVerificationTrackerFactory.create({})
  sessionManager.profile = rehydrateProfile(sessionManager.profile)
  sessionManager.allValidityRecords = rehydrateValidityRecords(sessionManager.allValidityRecords)
  // Request instances don't survive a JSON round-trip intact (RequestInstanceConfig reads its
  // step config from a `<key>Config` property that the serialised form no longer carries), so
  // reset them to empty instances rather than restore a half-populated object.
  sessionManager.allRequests = ClientRequestsFactory.create([])
  sessionManager.selectedRequest = ClientRequestInstanceFactory.create({})
  return { ...state, sessionManager }
}
